import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    Component,
    effect,
    inject,
    input,
    OnDestroy,
    signal,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { lockBodyScroll, unlockBodyScroll } from './body-scroll-lock';

/**
 * Body-mounted modal overlay: mounts projected content into a CDK Overlay at
 * `<body>` level so a `position: fixed` overlay escapes ancestor stacking
 * contexts. Driven purely by `[open]` — no id-registry, backdrop, or Esc close.
 */
@Component({
    selector: 'ft-modal-overlay',
    templateUrl: './ft-modal.component.html',
})
export class FtModalComponent implements AfterViewInit, OnDestroy {
    readonly open = input(false);

    private readonly content = viewChild.required<TemplateRef<unknown>>('content');
    private readonly overlay = inject(Overlay);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private overlayRef?: OverlayRef;
    // #content resolves only once the view exists; gate the first sync on it.
    private readonly viewReady = signal(false);

    constructor() {
        effect(() => {
            if (!this.viewReady()) return;
            if (this.open()) {
                this.attach();
            } else {
                this.detach();
            }
        });
    }

    ngAfterViewInit(): void {
        this.viewReady.set(true);
    }

    ngOnDestroy(): void {
        this.detach();
    }

    private attach(): void {
        if (this.overlayRef) return;
        this.overlayRef = this.overlay.create({
            hasBackdrop: false,
            // Background scroll is locked via <body> overflow:hidden (below), matching v1's
            // ftEnsureModalSingleScroll — it preserves document layout and scroll offset.
            // CDK's block() strategy instead puts position:fixed on <html>, collapsing the
            // document to one viewport shifted by the scroll offset, which diverges from v1
            // in full-page parity captures.
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            positionStrategy: this.overlay.position().global(),
        });
        this.overlayRef.attach(new TemplatePortal(this.content(), this.viewContainerRef));
        lockBodyScroll();
    }

    private detach(): void {
        if (!this.overlayRef) return;
        this.overlayRef.dispose();
        this.overlayRef = undefined;
        unlockBodyScroll();
    }
}
